import { z } from 'zod';
/**
 * The contract for `POST /v1/allocation/automations/demo-estate/reset`.
 *
 * The endpoint restores the synthetic demo estate — 13 founders, 22 doers, 9
 * assignments — to a fixed BEFORE state, deletes the residue the automations
 * leave behind, and draws (or keeps) the bench endorsement cutover baseline,
 * reported as one more row of `tables`. It is destructive, it is exposed on an admin screen,
 * and the same code ships in the production image, where an environment check
 * refuses it outright. See `apps/api/src/lib/allocation-demo-estate.ts`.
 */
/**
 * A typed phrase, not a boolean.
 *
 * `{ confirm: true }` is a field a client can set by accident, a replayed
 * request already carries, and a stray double-submit sends twice. This request
 * deletes automation run history and rewrites doer rows, so the body has to be
 * something a person deliberately typed. It is a `literal`, so a wrong phrase is
 * a schema failure — the route never has to compare it itself.
 */
export const ALLOCATION_DEMO_RESET_CONFIRM_PHRASE = 'RESET DEMO STATE';
export const allocationDemoEstateResetRequestSchema = z.object({
    confirm: z.literal(ALLOCATION_DEMO_RESET_CONFIRM_PHRASE),
}).strict();
/**
 * What happened to one table. One row of the screen's summary table.
 *
 * `restored` and `created` are kept apart because they answer different
 * questions: a reset that suddenly reports 14 `created` doers means the previous
 * estate was deleted rather than moved, and every id a screenshot or a saved
 * link referred to has changed. `cleared` is rows removed — the stray and
 * duplicate assignments on the estate tables, and the residue on the rest.
 *
 * A LIST rather than a field per table, because the set of tables a reset
 * touches is not a contract: adding a residue table is a change to this array's
 * contents, not to the schema, and a screen that renders rows renders the new
 * one without a deploy. `label` travels with the row so the screen never has to
 * hold a table-name-to-English map that drifts.
 */
export const allocationDemoEstateResetTableSchema = z.object({
    table: z.string(),
    label: z.string(),
    restored: z.number().int().min(0),
    created: z.number().int().min(0),
    cleared: z.number().int().min(0),
}).strict();
/**
 * The census, reported rather than merely checked.
 *
 * A screen that shows "3,412 -> 3,412 non-demo doers" tells an operator the
 * guard actually ran against a populated database. A guard that only speaks up
 * when it fails is indistinguishable from a guard that was accidentally
 * comparing zero to zero, and this one is the last line between a filter bug and
 * an unnoticed outage.
 */
export const allocationDemoEstateNonDemoRowsSchema = z.object({
    founders: z.number().int().min(0),
    doers: z.number().int().min(0),
    assignments: z.number().int().min(0),
}).strict();
export const allocationDemoEstateResetResponseSchema = z.object({
    tables: z.array(allocationDemoEstateResetTableSchema),
    /**
     * The HR notice receipts put back into the evidence store — three, always,
     * because they are a fixed part of the estate.
     *
     * Counted separately from the `app_settings` row even though they live in it:
     * that row's `cleared` is residue REMOVED, and adding a restore to it would
     * net two opposite operations into one number that describes neither.
     */
    receiptsRestored: z.number().int().min(0),
    nonDemoRowsBefore: allocationDemoEstateNonDemoRowsSchema,
    nonDemoRowsAfter: allocationDemoEstateNonDemoRowsSchema,
}).strict();
/**
 * The refusal codes a client must handle. Every one of them means nothing was
 * restored; `non_demo_rows_touched` additionally means something WAS written.
 */
export const allocationDemoEstateResetRefusalCodeSchema = z.enum([
    /** Guard 1 or 2: production, or the deploy-time gate is not `true`. */
    'demo_reset_unavailable',
    /**
     * Guard 3 or 4: a row outside the demo estate was reached, or the non-demo
     * census moved across the reset by more than the rows another process created
     * meanwhile (a movement that is exactly those rows answers 200). The
     * before/after counts are present for the census case, which means the reset
     * RAN, and absent when the refusal came before any write.
     */
    'non_demo_rows_touched',
    /**
     * The reset would delete more demo-only automation run history than it is
     * allowed to in one go. Checked before any write, so nothing was written.
     * Runs that touch real doers are kept and never count towards the limit.
     */
    'demo_history_too_large',
    /**
     * Another reset is running (it holds the `app_settings` lease). Nothing was
     * read or written. Retry once it has finished.
     */
    'demo_reset_in_progress',
    /**
     * The Allocation staging refresh holds its writer lock, so it is replacing
     * this database right now. Nothing was written. Retry once it has finished.
     */
    'staging_refresh_in_progress',
    /**
     * `allocation_refresh_in_progress()` could not be asked (missing, erroring or
     * a non-boolean answer). The reset fails closed. Nothing was written.
     */
    'staging_refresh_check_unavailable',
    /**
     * The bench endorsement tab could not be read (or returned nothing, or the
     * stored baseline is malformed), so the bench endorsement cutover baseline
     * the reset draws cannot be established. Checked before any write, so
     * nothing was written. Answered with 502: the fault is upstream.
     */
    'bench_baseline_unavailable',
    /**
     * The reset RAN — the estate was restored and its audit row written — but
     * the bench endorsement cutover baseline could not be written (a lost
     * compare-and-set, an update error). The only late refusal besides the
     * census. Running the reset again settles it.
     */
    'bench_baseline_settle_failed',
    /**
     * The reset RAN — the estate was restored and its audit row written — but
     * the automations could not be switched on in Preview (the `automation_flags`
     * upsert or its audit rows failed). The audit row records it. Switch them on
     * by hand or run the reset again.
     */
    'automation_flags_settle_failed',
]);
/**
 * The 409 body. Deliberately the SAME vocabulary as the success body: a client
 * that has learned to read `nonDemoRowsBefore`/`nonDemoRowsAfter` on the way out
 * reads the same two objects on the way in.
 *
 * Both are present only for the census case. A row refused BEFORE it was written
 * has no after-count to report, and inventing one would put a number next to a
 * reading that was never taken.
 */
export const allocationDemoEstateResetRefusalSchema = z.object({
    code: allocationDemoEstateResetRefusalCodeSchema,
    error: z.string(),
    nonDemoRowsBefore: allocationDemoEstateNonDemoRowsSchema.optional(),
    nonDemoRowsAfter: allocationDemoEstateNonDemoRowsSchema.optional(),
}).strict();
//# sourceMappingURL=allocation-demo-estate.js.map